import { CommonModule } from '@angular/common';
import { Component, ElementRef, ViewChild, OnDestroy, Input, NgZone, } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { AiInterviewSessionService } from '@edtech-ui/core/services/ai-interview-session-service/ai-interview-session.service';
import { WebrtcService } from '@edtech-ui/core/services/webrtc/webrtc.service';
import { firstValueFrom, interval, Subject, takeUntil } from 'rxjs';
import { ConversationState } from '@edtech-ui/constants/conversationState';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { ExitConfirmDialogComponent } from '../../exit-confirm-dialog/exit-confirm-dialog.component';

type ChatTurn = { role: 'assistant' | 'user'; content: string; ts: string };
import { AiModelName } from '@edtech-ui/constants/ai-model-name.constant';
import { FALLBACK_INTERVIEW_LANGUAGES, InterviewLanguage, parseScriptRanges } from '@edtech-ui/constants/interview-language.constant';

@Component({
  selector: 'app-step-interview',
  standalone: true,
  imports: [CommonModule, MatDialogModule],
  templateUrl: './step-interview.component.html',
  styleUrl: './step-interview.component.scss',
})
export class StepInterviewComponent implements OnDestroy {
  @ViewChild('videoElement', { static: false }) videoElement!: ElementRef<HTMLVideoElement>;
  @ViewChild('remoteAudio', { static: false }) remoteAudio!: ElementRef<HTMLAudioElement>;
  @Input() isNavigatingFromWizard = false;
  @ViewChild('chatWindow') chatWindow!: ElementRef<HTMLDivElement>;
  transcript: string[] = [];
  chatLog: ChatTurn[] = [];
  isConnected = false;
  timeRemaining = '00:00';
  isAnalyzing = false;
  private greetingSent = false;

  private recognition?: any;
  private sttActive = false;
  private aiPartial = '';
  private timer?: any;
  private startTime = Date.now();
  private destroy$ = new Subject<void>();
  private lastSynced = 0;
  private syncDebounceTimer: any;
  private interviewEnded = false;
  isNavigating: boolean = false;
  private interviewFinalized = false;
  private autoScroll = true;
  private scrollThreshold = 120;
  private scrollPending = false;
  private convoState = ConversationState.IDLE;
  public isAiSpeaking = false;
  private awaitingAiResponse = false;

  // Supported answer languages come from the backend language config (returned at session creation).
  // Their `script_ranges` form the whitelist: any character in them means the text is in a supported language.
  private get supportedLanguages(): InterviewLanguage[] {
    const configured = this.sessionService.value.supported_languages;
    return configured?.length ? configured : FALLBACK_INTERVIEW_LANGUAGES;
  }
  private supportedScriptRanges?: [number, number][];

  private hasSupportedScript(text: string): boolean {
    this.supportedScriptRanges ??= parseScriptRanges(this.supportedLanguages);
    for (const char of text) {
      const cp = char.codePointAt(0)!;
      if (this.supportedScriptRanges.some(([start, end]) => cp >= start && cp <= end)) return true;
    }
    return false;
  }

  // Blocklisted non-Latin scripts. Checked only after the supported-script whitelist,
  // so a configured language whose script appears here is still accepted.
  // Cyrillic, Bengali, Gurmukhi, Gujarati, Odia, Malayalam, Thai, Tibetan,
  // Japanese Hiragana/Katakana, CJK, Korean Hangul.
  private readonly UNSUPPORTED_SCRIPT = /[\u0400-\u04FF\u0980-\u09FF\u0A00-\u0AFF\u0B00-\u0B7F\u0D00-\u0DFF\u0E00-\u0EFF\u0F00-\u0FFF\u3040-\u30FF\u4E00-\u9FFF\uAC00-\uD7AF]/;

  // Latin-script signals for unsupported languages — words with no overlap with
  // English technical vocabulary. Built once at class level, not per-call.
  private readonly UNSUPPORTED_LATIN_SIGNALS: readonly string[][] = [
    // German
    ['ich', 'nicht', 'können', 'haben', 'werden', 'weil', 'wenn', 'dass',
      'aber', 'über', 'nach', 'beim', 'vom', 'zur', 'zum', 'ohne', 'schon',
      'noch', 'auch', 'damit', 'jedoch', 'bereits', 'obwohl', 'immer'],
    // French
    ['je', 'vous', 'nous', 'ils', 'elles', 'mais', 'avec', 'dans', 'notre',
      'votre', 'leur', 'très', 'alors', 'donc', 'puis', 'encore', 'bien',
      'aussi', 'même', 'ainsi', 'toujours', 'déjà', 'parfois'],
    // Spanish
    ['también', 'porque', 'cuando', 'donde', 'después', 'aquí', 'nosotros',
      'ellos', 'están', 'tiene', 'puede', 'hasta', 'entre', 'sobre', 'antes',
      'siempre', 'mismo', 'algo', 'quiero', 'esto'],
    // Italian
    ['sono', 'questa', 'anche', 'però', 'ancora', 'loro', 'ogni', 'dopo',
      'prima', 'quello', 'fatto', 'tutto', 'molto', 'bene', 'sempre'],
    // Portuguese
    ['também', 'porque', 'quando', 'onde', 'depois', 'nosso', 'eles',
      'estão', 'pode', 'até', 'sobre', 'antes', 'sempre', 'isso', 'aqui'],
  ];


  constructor(
    private toast: ToastrService,
    private webrtc: WebrtcService,
    public sessionService: AiInterviewSessionService,
    private router: Router,
    private ngZone: NgZone,
    private dialog: MatDialog


  ) { }

  getInterviewerImage(): string {
    const name = this.sessionService.value.interviewer_name;
    switch (name) {
      case AiModelName.PRIYA_SHARMA:
        return 'assets/images/interview_PriyaSharma.png';
      case AiModelName.SARAH_JOHNSON:
        return 'assets/images/interview_SarahJhonson.png';
      case AiModelName.ARJUN_PATEL:
        return 'assets/images/interview_Arjun Patel.png';
      case AiModelName.MICHAEL_CHEN:
        return 'assets/images/interview_MichaelChen.png';
      default:
        return 'assets/images/interview_PriyaSharma.png'; // Default
    }
  }

  async ngOnInit() {
    await this.initializeWebRTC();
    this.startTimer();
  }

  ngAfterViewInit() {
    if (!this.chatWindow) return;
    const chatEl = this.chatWindow.nativeElement;
    chatEl.addEventListener('scroll', () => {
      const atBottom =
        chatEl.scrollHeight - chatEl.scrollTop - chatEl.clientHeight < this.scrollThreshold;
      this.autoScroll = atBottom;
    });
  }



  canExitInterview(): boolean {
    return this.interviewEnded || this.interviewFinalized;
  }

  private appendMessage(role: 'assistant' | 'user', content: string) {
    this.chatLog.push({ role, content, ts: new Date().toISOString() });

    if (this.autoScroll) {
      if (!this.scrollPending) {
        this.scrollPending = true;
        requestAnimationFrame(() => {
          this.safeScrollToBottom();
          this.scrollPending = false;
        });
      }
    }
  }

  private safeScrollToBottom() {
    if (!this.chatWindow) return;
    const chatEl = this.chatWindow.nativeElement;
    chatEl.scrollTo({ top: chatEl.scrollHeight, behavior: 'smooth' });
  }

  ngOnDestroy() {
    this.cleanup();
  }

  /** -------------------- WebRTC Setup -------------------- */
  private async initializeWebRTC() {
    try {
      const session = this.sessionService.value;
      if (!session?.ephemeral_token || !session?.openai_api_url) {
        this.toast.error('No active interview session. Please restart.');
        return;
      }

      const localStream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        video: true,
      });

      this.videoElement.nativeElement.srcObject = localStream;
      this.videoElement.nativeElement.muted = true;

      const remoteStream = await this.webrtc.connect({
        webrtcConfig: session.webrtc_config!,
        apiUrl: session.openai_api_url || 'https://api.openai.com/v1/realtime/calls',
        model: session.model || 'gpt-realtime-mini-2025-12-15',
        ephemeralToken: session.ephemeral_token,
        localStream,
        onTrack: (e) => {
          const [stream] = e.streams;
          this.remoteAudio.nativeElement.srcObject = stream;
          this.remoteAudio.nativeElement.volume = 0.8;
        },
        onMessage: (e: MessageEvent) => this.handleAIEvent(e.data),
      });

      this.remoteAudio.nativeElement.srcObject = remoteStream;
      this.isConnected = true;
      this.toast.success('AI Interview started successfully!');
      this.startLocalTranscription();


      // Start AI greeting (Fallback if session.created event is delayed or skipped)
      setTimeout(() => {
        if (this.greetingSent) return;
        this.greetingSent = true;
        this.webrtc.send({ type: 'response.create' });
      }, 2000);

    } catch (err) {
      console.error('WebRTC init error:', err);
      this.toast.error('Failed to initialize AI Interview.');
    }
  }

  /** -------------------- Handle AI Events -------------------- */

  private handleAIEvent(data: string) {
    try {
      const message = JSON.parse(data);

      switch (message.type) {
        case 'session.created':
        case 'session.updated': {
          if (this.greetingSent) return;
          this.greetingSent = true;
          this.webrtc.send({ type: 'response.create' });
          break;
        }

        // Beta API (fallback): response.audio_transcript.done
        case 'response.output_audio_transcript.done':
        case 'response.audio_transcript.done': {
          const transcript = message.transcript?.trim();
          if (transcript) {
            this.appendMessage('assistant', transcript);
            this.transcript.push(`🤖 AI: ${transcript}`);
          }
          this.stopDubbVideo();
          this.isAiSpeaking = false;
          break;
        }

        // GA API: output_audio_buffer.started fires when AI begins speaking
        case 'output_audio_buffer.started':
        case 'response.audio.delta':
        case 'response.output_audio.delta': {
          if (!this.isAiSpeaking) {
            this.playDubbVideo();
            this.isAiSpeaking = true;
          }
          break;
        }

        // GA API: output_audio_buffer.stopped fires when AI finishes speaking
        case 'output_audio_buffer.stopped':
        case 'response.audio.done':
        case 'response.output_audio.done': {
          this.stopDubbVideo();
          this.isAiSpeaking = false;
          break;
        }

        case 'conversation.item.input_audio_transcription.completed': {
          const transcript = message.transcript?.trim();
          if (transcript) {
            this.appendMessage('user', transcript);
            this.transcript.push(`🧑 You: ${transcript}`);
            if (this.isUnsupportedLanguage(transcript)) {
              this.enforceLanguageBlock();
            }
          }
          break;
        }
        default:
          break;
      }
    } catch (error) {
      console.warn('AI Event Parsing Error:', error);
    }
  }

  private startUserMic() {
    if (this.convoState !== ConversationState.USER_SPEAKING) return;
    this.recognition?.start();
  }

  private stopUserMic() {
    try { this.recognition?.stop(); } catch { }
  }


  private playDubbVideo() {
    const videoEl = this.videoElement?.nativeElement;
    if (videoEl) videoEl.classList.add('ai-speaking');
  }

  private stopDubbVideo() {
    const videoEl = this.videoElement?.nativeElement;
    if (videoEl) videoEl.classList.remove('ai-speaking');
  }

  /**
   * Returns true only when the transcription is in a language that is NOT
   * supported — i.e. not one of the configured supported answer languages.
   *
   * Step 1: Whitelist — any character in a supported language's script_ranges → allowed.
   * Step 2: Block — unsupported non-Latin script present → blocked.
   * Step 3: Block — unsupported Latin-script language detected via class-level
   *         function-word lists (built once, not per-call).
   */
  private isUnsupportedLanguage(text: string): boolean {
    const words = text.trim().split(/\s+/);
    if (words.length < 4) return false;

    if (this.hasSupportedScript(text)) return false;
    if (this.UNSUPPORTED_SCRIPT.test(text)) return true;

    const normalized = words.map(w => w.toLowerCase().replace(/[^a-zäöüàâéêèîïôùûçß]/g, ''));
    for (const langWords of this.UNSUPPORTED_LATIN_SIGNALS) {
      const matches = normalized.filter(w => w.length > 1 && langWords.includes(w)).length;
      if (matches >= 2) return true;
    }

    return false;
  }

  /**
   * Hard-blocks the model when the candidate uses a completely unsupported
   * language. Cancels any in-progress response and forces the block message.
   */
  private enforceLanguageBlock(): void {
    // Instruction text is rendered by the backend for the session's selected language.
    const blockInstruction = this.sessionService.value.runtime_instructions?.language_block;
    if (!blockInstruction) return;
    this.webrtc.send({ type: 'response.cancel' });

    setTimeout(() => {
      this.webrtc.send({
        type: 'response.create',
        response: {
          instructions: blockInstruction,
          modalities: ['text', 'audio'],
        },
      });
    }, 150);
  }

  private startLocalTranscription() {
    const w = window as any;
    const SR = w.SpeechRecognition || w.webkitSpeechRecognition;
    if (!SR) return;

    this.recognition = new SR();
    this.recognition.lang = 'en-IN';
    this.recognition.interimResults = true;
    this.recognition.continuous = true;



    this.recognition.onerror = (err: any) => {
      console.warn('SpeechRecognition error:', err.error);
    };
    this.recognition.onend = () => {
      if (this.sttActive) setTimeout(() => this.recognition.start(), 500);
    };

    this.sttActive = true;
    this.recognition.start();
  }


  private syncConversationToServer() {
    if (this.interviewFinalized) return; // stop syncing after end

    const session = this.sessionService.value;
    if (!session.session_id || this.chatLog.length === 0) return;

    const now = Date.now();
    if (now - this.lastSynced < 3000) return;
    this.lastSynced = now;

    const payload = {
      session_id: session.session_id,
      conversation_json: this.chatLog.map((t) => ({
        role: t.role,
        content: t.content,
        timestamp: t.ts,
      })),
    };

    this.sessionService.updateConversation(payload).subscribe({
      next: (res) => {
        if (!res.Success) console.warn('Sync failed:', res.Message);
      },
      error: (err) => {
        if (err.status === 401) {
          console.warn('Auth expired, skipping sync.');
        } else if (err.message?.includes('No primary')) {
        } else {
          console.error('Sync error:', err);
        }
      },
    });
  }

  private startTimer() {
    const session = this.sessionService.value;
    const totalMinutes = session.duration_minutes || 20;
    const totalSeconds = totalMinutes * 60;
    this.startTime = Date.now();

    this.timer = setInterval(() => {
      const elapsed = Math.floor((Date.now() - this.startTime) / 1000);
      const remaining = totalSeconds - elapsed;

      if (remaining <= 0 && !this.interviewEnded) {
        clearInterval(this.timer);
        this.interviewEnded = true;
        this.autoSubmitInterview();
      } else if (remaining > 0) {
        const min = Math.floor(remaining / 60);
        const sec = remaining % 60;
        this.timeRemaining = `${min}:${sec.toString().padStart(2, '0')}`;
      }
    }, 1000);
  }

  private async submitExitInterview(): Promise<void> {
    if (this.hasFinalUpdateRun) return;
    this.interviewEnded = true;
    this.toast.info('Ending interview... Please wait.');
    this.forceStopRealtime();
    await this.finishInterview();
  }


  openExitInterviewDialog(): void {
    const dialogRef = this.dialog.open(ExitConfirmDialogComponent, {
      width: '26rem',
      disableClose: true,
      data: {
        title: 'Exit Interview',
        message:
          'Once you submit the interview, you will receive feedback on your interview performance.',
        confirmText: 'Exit',
        cancelText: 'Cancel',
        icon: 'exit_to_app',
      },
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.submitExitInterview();
      }
    });
  }


  private async autoSubmitInterview(): Promise<void> {
    this.interviewEnded = true;
    this.toast.info('Time is up! Submitting your interview...');
    this.forceStopRealtime();
    await this.finishInterview(); // Final update done here
  }

  private forceStopRealtime() {
    try {
      this.recognition?.stop();
      this.webrtc.close();
      this.isConnected = false;

      const videoEl = this.videoElement?.nativeElement;
      if (videoEl?.srcObject) {
        (videoEl.srcObject as MediaStream).getTracks().forEach((t) => t.stop());
        videoEl.srcObject = null;
      }

      const audioEl = this.remoteAudio?.nativeElement;
      if (audioEl?.srcObject) {
        (audioEl.srcObject as MediaStream).getTracks().forEach((t) => t.stop());
        audioEl.srcObject = null;
      }
    } catch (e) {
    }
  }
  hasFinalUpdateRun = false;

  private async finishInterview(): Promise<void> {
    if (this.hasFinalUpdateRun) {
      return;
    }
    this.hasFinalUpdateRun = true;

    this.interviewFinalized = true;
    this.isAnalyzing = true;

    clearTimeout(this.syncDebounceTimer);
    clearInterval(this.timer);
    this.destroy$.next();
    this.destroy$.complete();
    this.sttActive = false;

    try { this.recognition?.stop(); } catch { }

    const session = this.sessionService.value;
    if (!session?.session_id) {
      this.toast.error('Missing session information.');
      this.isAnalyzing = false;
      return;
    }

    try {
      // STEP 1: Push conversation ONCE at the end
      const finalPayload = {
        session_id: session.session_id,
        conversation_json: this.chatLog.map((t) => ({
          role: t.role,
          content: t.content,
          timestamp: t.ts,
        })),
      };

      const updateRes = await firstValueFrom(this.sessionService.updateConversation(finalPayload));

      const endRes = await firstValueFrom(
        this.sessionService.endInterview({ session_id: session.session_id })
      );
      if (!endRes?.Success) return;
      this.interviewFinalized = true;
      let evalRes: any = null;
      const maxWait = Date.now() + 30000;
      while (Date.now() < maxWait && !evalRes) {
        try {
          evalRes = await firstValueFrom(this.sessionService.evaluateInterview(session.session_id));
          if (evalRes?.Success) break;
        } catch {
          await new Promise((r) => setTimeout(r, 3000));
        }
      }

      if (evalRes?.Success && evalRes.Data) {
        localStorage.setItem('last_evaluation', JSON.stringify(evalRes.Data));
        localStorage.setItem('last_session_id', session.session_id);
        this.ngZone.run(() => {
          this.isAnalyzing = false;
          this.router.navigateByUrl('/student-dashboard/ai-interview-performance');
        });
      } else {
        this.isAnalyzing = false;
        this.toast.error('Evaluation failed. Please retry from dashboard.');
        this.router.navigateByUrl('/student-dashboard/ai-interview-dashboard');
      }
    } catch (err) {
      console.error('finishInterview error:', err);
      this.isAnalyzing = false;
      this.toast.error('Interview could not complete.');
    }
  }

  trackByIndex(index: number, _item: any): number {
    return index;
  }

  private cleanup() {
    this.sttActive = false;
    this.destroy$.next();
    this.destroy$.complete();

    try {
      this.recognition?.stop();
      this.webrtc.close();
    } catch { }

    clearInterval(this.timer);
    clearTimeout(this.syncDebounceTimer);
    if (!this.isNavigating) this.sessionService.reset();
  }
}
